/* RAVIN / Full-screen image deck v1.
   Adds an independent viewing layer. Original PDP scroll deck and mobile Arc Fan are untouched.
   Clean images only: NO decorative strokes or graphics over product photography. */
(() => {
  if (window.__RavinFullscreenDeckLoaded) return;
  window.__RavinFullscreenDeckLoaded = true;

  const rootSelector = '[data-ravin-pdp-dual-deck]';
  const activeCardSelector = '[data-ravin-deck-card].is-active, [data-ravin-arc-card].is-active';
  const instances = new WeakMap();
  const pageReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pad = n => String(n).padStart(2,'0');
  const mod = (n,total) => ((n % total) + total) % total;

  function init(root) {
    if (!root || instances.has(root)) return;
    const desktopCards = Array.from(root.querySelectorAll('[data-ravin-deck-card]'));
    if (!desktopCards.length) return;
    const media = desktopCards.map(card => {
      const image = card.querySelector('img');
      const w = Number(image?.getAttribute('width')) || image?.naturalWidth || 0;
      const h = Number(image?.getAttribute('height')) || image?.naturalHeight || 0;
      const aspect = w > 0 && h > 0 && (w / h) > .25 && (w / h) < 4 ? w / h : .75;
      return {
        src: card.dataset.rvViewerSrc || image?.src || '',
        thumb: image?.currentSrc || image?.src || '',
        alt: card.dataset.rvViewerAlt || image?.alt || '',
        aspect
      };
    });
    if (!media.some(x => x.src)) return;

    const abort = new AbortController();
    const signal = abort.signal;
    const arabic = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
    const labels = arabic ? {
      heading:'ريفين / الصور',dialog:'عارض صور المنتج',close:'إغلاق الصور',
      prev:'الصورة السابقة',next:'الصورة التالية',swipe:'اسحب للتنقل بين الصور',
      keyboard:'استخدم الأسهم للتنقل',open:'عرض الصورة بحجم كامل'
    } : {
      heading:'RAVIN / IMAGE DECK',dialog:'Full-screen product image deck',close:'Close images',
      prev:'Previous image',next:'Next image',swipe:'SWIPE TO EXPLORE',
      keyboard:'USE ARROWS TO EXPLORE',open:'Open full-screen image'
    };
    let viewer = null, nodes = null, activeIndex = 0, triggeringElement = null;
    let originalBody = null, scrollY = 0, suppressTapUntil = 0;
    let arcPosition = 0, animFrame = 0;
    let modalController = null;

    function portal() {
      if (viewer) return;
      viewer = document.createElement('div');
      viewer.className = 'rv-image-deck';
      viewer.setAttribute('role','dialog');
      viewer.setAttribute('aria-modal','true');
      viewer.setAttribute('aria-label',labels.dialog);
      viewer.setAttribute('tabindex','-1');
      viewer.hidden = true;
      viewer.innerHTML = [
        "<div class=\"rv-image-deck__scrim\" data-rv-viewer-close-backdrop></div>",
        "<div class=\"rv-image-deck__interface\">",
        "<header class=\"rv-image-deck__top\">",
        "<div class=\"rv-image-deck__brand\"><span class=\"rv-image-deck__kicker\"></span><span class=\"rv-image-deck__progress\" aria-live=\"polite\"></span></div>",
        "<button class=\"rv-image-deck__close\" type=\"button\" data-rv-viewer-close aria-label=\"Close\">×</button>",
        "</header>",
        "<div class=\"rv-image-deck__stage\" data-rv-viewer-stage>",
        "<div class=\"rv-image-deck__arc\" data-rv-viewer-arc></div>",
        "<button class=\"rv-image-deck__arrow rv-image-deck__arrow--prev\" type=\"button\" data-rv-viewer-prev aria-label=\"Previous\">‹</button>",
        "<button class=\"rv-image-deck__arrow rv-image-deck__arrow--next\" type=\"button\" data-rv-viewer-next aria-label=\"Next\">›</button>",
        "</div>",
        "<footer class=\"rv-image-deck__bottom\"><span class=\"rv-image-deck__counter\" aria-live=\"polite\"></span><span class=\"rv-image-deck__hint\"></span></footer>",
        "</div>"
      ].join('');
      document.body.appendChild(viewer);
      nodes = {
        close: viewer.querySelector('[data-rv-viewer-close]'),
        backdrop: viewer.querySelector('[data-rv-viewer-close-backdrop]'),
        prev: viewer.querySelector('[data-rv-viewer-prev]'),
        next: viewer.querySelector('[data-rv-viewer-next]'),
        stage: viewer.querySelector('[data-rv-viewer-stage]'),
        arc: viewer.querySelector('[data-rv-viewer-arc]'),
        topCount: viewer.querySelector('.rv-image-deck__progress'),
        bottomCount: viewer.querySelector('.rv-image-deck__counter')
      };
      nodes.cards = media.map((item,index)=>{
        const card=document.createElement('figure');
        card.className='rv-image-deck__card';
        card.dataset.imageIndex=String(index);
        card.setAttribute('aria-hidden','true');
        const img=document.createElement('img');
        img.className='rv-image-deck__photo';
        img.alt=item.alt||'';
        img.draggable=false;
        img.decoding='async';
        img.loading=index===0?'eager':'lazy';
        img.src=item.thumb||item.src;
        card.appendChild(img);
        nodes.arc.appendChild(card);
        return card;
      });
      viewer.querySelector('.rv-image-deck__kicker').textContent = labels.heading;
      viewer.querySelector('.rv-image-deck__hint').textContent = labels.swipe;
      nodes.close.setAttribute('aria-label',labels.close);
      nodes.prev.setAttribute('aria-label',labels.prev);
      nodes.next.setAttribute('aria-label',labels.next);
      viewer.classList.toggle('rv-image-deck--single', media.length === 1);
    }

    const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
    const ease=t=>1-Math.pow(1-t,3);
    // One continuous full-image view: maximize available size, never crop/zoom the image.
    function fitCards(){
      if(!nodes?.stage || !nodes.cards?.length)return;
      const rect=nodes.stage.getBoundingClientRect();
      const mobile=window.matchMedia('(max-width:989px)').matches;
      const roomW=Math.max(200,(rect.width || window.innerWidth)*(mobile?.985:.945));
      const roomH=Math.max(200,(rect.height || window.innerHeight)-(mobile?4:8));
      nodes.cards.forEach((card,i)=>{
        const aspect=clamp(media[i]?.aspect||.75,.3,3.5);
        const height=Math.min(roomH,roomW/aspect);
        const width=Math.min(roomW,height*aspect);
        card.style.setProperty('--rv-arc-fit-w',width.toFixed(1)+'px');
        card.style.setProperty('--rv-arc-fit-h',height.toFixed(1)+'px');
      });
    }
    function paint(position){
      if(!nodes)return;
      const total=media.length;
      const width=nodes.stage.clientWidth||window.innerWidth;
      const mobile=window.matchMedia('(max-width:989px)').matches;
      const spread=mobile?Math.min(width*.49,205):Math.min(width*.315,320);
      nodes.cards.forEach((card,i)=>{
        const direct=i-position;
        const offset=direct-total*Math.round(direct/total);
        const d=Math.abs(offset);
        const x=offset*spread,z=-d*145,rise=d*10;
        const yaw=-offset*(mobile?19:14);
        const roll=offset*(mobile?8.2:6.1);
        const scale=1-Math.min(.29,d*(mobile?.155:.135));
        const opacity=d<1.85?clamp(1-Math.max(0,d-.65)*.55,0,1):0;
        card.style.setProperty('--rv-arc-x',x.toFixed(2)+'px');
        card.style.setProperty('--rv-arc-z',z.toFixed(2)+'px');
        card.style.setProperty('--rv-arc-rise',rise.toFixed(2)+'px');
        card.style.setProperty('--rv-arc-yaw',yaw.toFixed(2)+'deg');
        card.style.setProperty('--rv-arc-roll',roll.toFixed(2)+'deg');
        card.style.setProperty('--rv-arc-scale',scale.toFixed(4));
        card.style.opacity=String(opacity);
        card.style.zIndex=String(1000-Math.round(d*100));
        card.style.pointerEvents=d<.48?'auto':'none';
        const hero=d<.5;
        card.setAttribute('aria-hidden',String(!hero));
        card.classList.toggle('is-hero',hero);
        const image=card.querySelector('img');
        if(image && image.dataset.full!==String(hero)){
          image.dataset.full=String(hero);
          image.src=hero?media[i].src:(media[i].thumb||media[i].src);
        }
      });
    }
    function update(){
      if(!viewer)return;
      const count=pad(activeIndex+1)+' / '+pad(media.length);
      nodes.topCount.textContent=count;
      nodes.bottomCount.textContent=count;
      nodes.prev.disabled=media.length<2;
      nodes.next.disabled=media.length<2;
      paint(arcPosition);
    }
    function stopAnimation(){
      if(animFrame)window.cancelAnimationFrame(animFrame);
      animFrame=0;
    }
    function animateTo(destination,duration=510){
      stopAnimation();
      if(pageReduced.matches){
        arcPosition=mod(destination,media.length);
        paint(arcPosition);
        return;
      }
      const origin=arcPosition,start=performance.now();
      const tick=now=>{
        const t=clamp((now-start)/duration,0,1);
        arcPosition=origin+(destination-origin)*ease(t);
        paint(arcPosition);
        if(t<1)animFrame=window.requestAnimationFrame(tick);
        else{
          animFrame=0;
          arcPosition=mod(destination,media.length);
          paint(arcPosition);
        }
      };
      animFrame=window.requestAnimationFrame(tick);
    }
    function advance(delta){
      if(!viewer||viewer.hidden||media.length<2)return;
      activeIndex=mod(activeIndex+delta,media.length);
      update();
      animateTo(Math.round(arcPosition)+delta,520);
    }

    function lockScroll() {
      scrollY = window.scrollY;
      const body = document.body;
      originalBody = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow
      };
      body.style.position='fixed';
      body.style.top=(-scrollY)+'px';
      body.style.left='0';
      body.style.right='0';
      body.style.width='100%';
      body.style.overflow='hidden';
    }
    function unlockScroll() {
      if (!originalBody) return;
      const body=document.body;
      for (const [property,value] of Object.entries(originalBody)) body.style[property]=value;
      originalBody=null;
      window.scrollTo(0,scrollY);
    }

    function close() {
      if (!viewer || viewer.hidden) return;
      modalController?.abort();
      modalController=null;
      stopAnimation();
      viewer.hidden=true;
      unlockScroll();
      if (triggeringElement?.isConnected) triggeringElement.focus({preventScroll:true});
      triggeringElement=null;
    }

    function open(index,trigger) {
      // Only one photo viewer per document can be open at once.
      document.querySelectorAll('.rv-image-deck:not([hidden])').forEach(other=>{
        if (other !== viewer) other.querySelector('[data-rv-viewer-close]')?.click();
      });
      portal();
      if (!viewer.hidden) return;
      activeIndex=mod(index,media.length);
      arcPosition=activeIndex;
      triggeringElement=trigger;
      lockScroll();
      viewer.hidden=false;
      fitCards();
      modalController=new AbortController();
      const scoped=modalController.signal;
      window.addEventListener('resize',()=>{
        fitCards();
        paint(arcPosition);
      },{signal:scoped});
      nodes.close.addEventListener('click',close,{signal:scoped});
      nodes.backdrop.addEventListener('click',close,{signal:scoped});
      nodes.prev.addEventListener('click',()=>advance(-1),{signal:scoped});
      nodes.next.addEventListener('click',()=>advance(1),{signal:scoped});
      document.addEventListener('keydown',event=>{
        if (!viewer || viewer.hidden) return;
        if (event.key==='Escape') {event.preventDefault();close();return}
        if (event.key==='ArrowLeft'||event.key==='ArrowRight'){
          event.preventDefault();
          const delta=event.key==='ArrowLeft'?-1:1;
          advance(arabic?-delta:delta);
          return;
        }
        if (event.key==='Tab'){
          const controls=Array.from(viewer.querySelectorAll('button:not([disabled])'));
          const first=controls[0],last=controls[controls.length-1];
          if (event.shiftKey&&(document.activeElement===first||document.activeElement===viewer)){
            event.preventDefault();last?.focus();
          } else if (!event.shiftKey&&document.activeElement===last){
            event.preventDefault();first?.focus();
          }
        }
      },{signal:scoped});

      let gesture=null, ignoreStageClickUntil=0;
      const spread=()=>window.matchMedia('(max-width:989px)').matches?
        Math.min((nodes.stage.clientWidth||window.innerWidth)*.49,205):
        Math.min((nodes.stage.clientWidth||window.innerWidth)*.315,320);
      nodes.stage.addEventListener('pointerdown',event=>{
        // On phones, Touch Events below own swiping: iOS often cancels Pointer Events mid-drag.
        if(event.pointerType==='touch'||event.target.closest('button')||!event.isPrimary||
          (event.pointerType==='mouse'&&event.button!==0))return;
        gesture={id:event.pointerId,x:event.clientX,y:event.clientY,
          index:activeIndex,position:arcPosition,horizontal:false,vertical:false};
      },{signal:scoped});
      nodes.stage.addEventListener('pointermove',event=>{
        if(!gesture||gesture.id!==event.pointerId)return;
        const dx=event.clientX-gesture.x,dy=event.clientY-gesture.y;
        if(!gesture.horizontal&&!gesture.vertical){
          if(Math.abs(dy)>11&&Math.abs(dy)>Math.abs(dx)*1.25)gesture.vertical=true;
          else if(Math.abs(dx)>9&&Math.abs(dx)>Math.abs(dy)*1.16){
            gesture.horizontal=true;stopAnimation();
            nodes.stage.setPointerCapture?.(event.pointerId);
          }
        }
        if(!gesture.horizontal||media.length<2)return;
        if(event.cancelable)event.preventDefault();
        arcPosition=gesture.position-dx/Math.max(100,spread());
        paint(arcPosition);
      },{signal:scoped});
      nodes.stage.addEventListener('pointerup',event=>{
        if(!gesture||gesture.id!==event.pointerId)return;
        const start=gesture;gesture=null;
        const dx=event.clientX-start.x,dy=event.clientY-start.y;
        if(start.horizontal&&media.length>1){
          ignoreStageClickUntil=performance.now()+420;
          const direction=Math.abs(dx)>39&&Math.abs(dx)>Math.abs(dy)*1.1?
            (dx<0?1:-1):0;
          activeIndex=mod(start.index+direction,media.length);
          update();
          animateTo(start.position+direction,direction?370:270);
        }
      },{signal:scoped});
      const cancelGesture=()=>{
        if(gesture?.horizontal){
          activeIndex=gesture.index;
          animateTo(gesture.position,270);
        }
        gesture=null;
      };
      nodes.stage.addEventListener('pointercancel',cancelGesture,{signal:scoped});
      nodes.stage.addEventListener('lostpointercapture',event=>{
        if(gesture&&gesture.id===event.pointerId)cancelGesture();
      },{signal:scoped});
      // Native touch handlers guarantee iOS Safari swipes, including on the photo itself.
      let touch=null;
      nodes.stage.addEventListener('touchstart',event=>{
        if(event.touches.length!==1||event.target.closest('button'))return;
        const point=event.touches[0];
        touch={x:point.clientX,y:point.clientY,index:activeIndex,
          position:arcPosition,horizontal:false,vertical:false};
      },{passive:true,signal:scoped});
      nodes.stage.addEventListener('touchmove',event=>{
        if(!touch || event.touches.length!==1)return;
        const point=event.touches[0];
        const dx=point.clientX-touch.x,dy=point.clientY-touch.y;
        if(!touch.horizontal && !touch.vertical){
          if(Math.abs(dy)>11 && Math.abs(dy)>Math.abs(dx)*1.2)touch.vertical=true;
          else if(Math.abs(dx)>7 && Math.abs(dx)>Math.abs(dy)*1.07){
            touch.horizontal=true;
            stopAnimation();
          }
        }
        if(!touch.horizontal || media.length<2)return;
        if(event.cancelable)event.preventDefault();
        arcPosition=touch.position-dx/Math.max(110,spread()*.88);
        paint(arcPosition);
      },{passive:false,signal:scoped});
      nodes.stage.addEventListener('touchend',event=>{
        if(!touch)return;
        const state=touch;touch=null;
        const point=event.changedTouches[0];
        if(!point || !state.horizontal || media.length<2)return;
        const dx=point.clientX-state.x,dy=point.clientY-state.y;
        ignoreStageClickUntil=performance.now()+650;
        const direction=Math.abs(dx)>28 && Math.abs(dx)>Math.abs(dy)*.85?
          (dx<0?1:-1):0;
        activeIndex=mod(state.index+direction,media.length);
        update();
        animateTo(state.position+direction,direction?430:270);
      },{passive:true,signal:scoped});
      nodes.stage.addEventListener('touchcancel',event=>{
        if(!touch)return;
        const state=touch;touch=null;
        if(state.horizontal){
          activeIndex=state.index;
          animateTo(state.position,260);
        }
      },{passive:true,signal:scoped});
      nodes.stage.addEventListener('click',event=>{
        if(event.target===nodes.stage&&performance.now()>ignoreStageClickUntil)close();
      },{signal:scoped});
      nodes.arc.addEventListener('dragstart',event=>event.preventDefault(),{signal:scoped});
      update();
      nodes.close.focus({preventScroll:true});
    }

    root.addEventListener('click',event=>{
      const link=event.target.closest('.ravin-deck__card-link,.ravin-arc__card-link');
      if (!link || !root.contains(link) || performance.now()<suppressTapUntil) return;
      const card=link.closest('[data-ravin-deck-card],[data-ravin-arc-card]');
      if (!card || !card.classList.contains('is-active')) return;
      const index=Number(card.dataset.ravinDeckIndex ?? card.dataset.ravinArcIndex);
      if (!Number.isInteger(index) || !media[index]?.src) return;
      event.preventDefault();
      open(index,link);
    },{signal});

    root.addEventListener('keydown',event=>{
      if (event.key!=='Enter'&&event.key!==' ') return;
      const link=event.target.closest('.ravin-deck__card-link,.ravin-arc__card-link');
      if (!link || !root.contains(link)) return;
      const card=link.closest('[data-ravin-deck-card],[data-ravin-arc-card]');
      if (!card?.classList.contains('is-active')) return;
      const index=Number(card.dataset.ravinDeckIndex ?? card.dataset.ravinArcIndex);
      if (!Number.isInteger(index)||!media[index]?.src)return;
      event.preventDefault();
      open(index,link);
    },{signal});

    let touchStart=null,pointerStart=null;
    root.addEventListener('touchstart',event=>{
      if (event.touches.length!==1 || !event.target.closest('.ravin-arc__card-link')) return;
      const touch=event.touches[0];touchStart={x:touch.clientX,y:touch.clientY};
    },{passive:true,signal});
    root.addEventListener('touchend',event=>{
      if(!touchStart)return;
      const touch=event.changedTouches[0];
      if (touch&&Math.hypot(touch.clientX-touchStart.x,touch.clientY-touchStart.y)>10)
        suppressTapUntil=performance.now()+480;
      touchStart=null;
    },{passive:true,signal});
    root.addEventListener('touchcancel',()=>{touchStart=null},{passive:true,signal});
    root.addEventListener('pointerdown',event=>{
      if (event.pointerType==='touch'||event.button!==0 || !event.target.closest('.ravin-deck__card-link,.ravin-arc__card-link'))return;
      pointerStart={id:event.pointerId,x:event.clientX,y:event.clientY};
    },{signal});
    root.addEventListener('pointerup',event=>{
      if(!pointerStart||pointerStart.id!==event.pointerId)return;
      if(Math.hypot(event.clientX-pointerStart.x,event.clientY-pointerStart.y)>10)
        suppressTapUntil=performance.now()+480;
      pointerStart=null;
    },{signal});

    instances.set(root,()=>{
      close();
      viewer?.remove();
      viewer=null;
      abort.abort();
      instances.delete(root);
    });
  }
  function initScope(scope){
    if(scope.matches?.(rootSelector))init(scope);
    scope.querySelectorAll?.(rootSelector).forEach(init);
  }
  function disposeScope(scope){
    const roots=[];
    if(scope.matches?.(rootSelector)) roots.push(scope);
    scope.querySelectorAll?.(rootSelector).forEach(x=>roots.push(x));
    roots.forEach(x=>instances.get(x)?.());
  }
  if(document.readyState==='loading')
    document.addEventListener('DOMContentLoaded',()=>initScope(document),{once:true});
  else initScope(document);
  document.addEventListener('shopify:section:load',event=>initScope(event.target));
  document.addEventListener('shopify:section:unload',event=>disposeScope(event.target));
})();
